import { useState, useEffect, useRef } from 'react';
import { 
  getMonth, getExpenses, getBudgets, getAdvances, getRepaymentsForAdvance, getSettings,
  Month, Expense, Budget, Advance, Repayment, HouseholdSettings 
} from '../services/supabaseService';

export const useSupabaseData = (currentDate: Date) => {
  const [loading, setLoading] = useState(true);
  const fetchIndexRef = useRef(0);
  
  const [settings, setSettings] = useState<HouseholdSettings | null>(null);
  const [activeMonth, setActiveMonth] = useState<Month | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [advances, setAdvances] = useState<(Advance & { repayments: Repayment[] })[]>([]);

  const fetchData = async (silent = false) => {
    const currentFetchIndex = ++fetchIndexRef.current;
    if (!silent) setLoading(true);
    try {
      // Fetch Settings
      const settingsData = await getSettings();
      if (currentFetchIndex !== fetchIndexRef.current) return;
      setSettings(settingsData);

      // Fetch Month (Format: YYYY-MM)
      const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const monthData = await getMonth(monthKey);
      if (currentFetchIndex !== fetchIndexRef.current) return;
      setActiveMonth(monthData);

      if (monthData) {
        // Fetch Expenses and Budgets for this month
        const [expensesData, budgetsData] = await Promise.all([
          getExpenses(monthData.id),
          getBudgets(monthData.id)
        ]);
        if (currentFetchIndex !== fetchIndexRef.current) return;
        setExpenses(expensesData);
        setBudgets(budgetsData);
      } else {
        if (currentFetchIndex !== fetchIndexRef.current) return;
        setExpenses([]);
        setBudgets([]);
      }

      // Fetch Advances
      const advancesData = await getAdvances();
      if (currentFetchIndex !== fetchIndexRef.current) return;
      const advancesWithRepayments = await Promise.all(
        advancesData.map(async (adv) => {
          const reps = await getRepaymentsForAdvance(adv.id);
          return { ...adv, repayments: reps };
        })
      );
      if (currentFetchIndex !== fetchIndexRef.current) return;
      setAdvances(advancesWithRepayments);
      
    } catch (err) {
      console.error("Erreur lors de la récupération des données:", err);
    } finally {
      if (currentFetchIndex === fetchIndexRef.current) {
        if (!silent) setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(false);
  }, [currentDate]);

  const refreshData = (silent = false) => {
    fetchData(silent);
  };

  return {
    loading,
    settings,
    activeMonth,
    expenses,
    budgets,
    advances,
    refreshData
  };
};
